import { useEffect, useRef, useState } from 'react';

import { PolygonF, PolylineF } from '@react-google-maps/api';

import { useMapContext } from '@/contexts';
import { IMultiPolygon, IPolygon } from '@/models';
import { colors } from '@/theme';

const polygonOptions = {
  fillColor: colors.primary.light,
  strokeColor: colors.primary.main,
  editable: true,
  zIndex: 1,
};

interface DrawControlProps {
  polygonData?: IPolygon | IMultiPolygon | null;
  onPolygonComplete?: (newPolygonData: IPolygon | IMultiPolygon) => void;
  allowMultiple?: boolean; // Allow drawing multiple polygons for MultiPolygon
}

// The Maps JS API drawing library (DrawingManager) was retired in May 2026
// (https://developers.google.com/maps/deprecations), so polygon drawing is
// implemented here directly on top of map click events: the control button
// toggles drawing mode, each map click adds a vertex, double-click (or the
// button again) completes the polygon, Escape cancels.
export const DrawControl = (props: DrawControlProps) => {
  const { polygonData, onPolygonComplete, allowMultiple = true } = props;
  const { map } = useMapContext();

  const [isDrawing, setIsDrawing] = useState(false);
  const [vertices, setVertices] = useState<google.maps.LatLngLiteral[]>([]);
  const [cursor, setCursor] = useState<google.maps.LatLngLiteral | null>(null);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const isDrawingRef = useRef(isDrawing);
  const verticesRef = useRef(vertices);
  verticesRef.current = vertices;

  // Convert GeoJSON coordinates to Google Maps paths format
  const getPolygonPaths = () => {
    if (!polygonData) return [[]];

    if (polygonData.type === 'Polygon') {
      // For Polygon: single polygon with rings
      return polygonData.coordinates[0].map((coordinate) => ({
        lng: coordinate[0],
        lat: coordinate[1],
      }));
    } else {
      // For MultiPolygon: multiple polygons
      const firstPolygon = polygonData.coordinates[0];
      if (firstPolygon && firstPolygon[0]) {
        return firstPolygon[0].map((coordinate) => ({
          lng: coordinate[0],
          lat: coordinate[1],
        }));
      }
      return [[]];
    }
  };

  const polygonPaths = getPolygonPaths();

  const handlePolygonComplete = (polygon: google.maps.Polygon) => {
    const polygonBounds = polygon.getPath();
    const bounds = [];
    for (let i = 0; i < polygonBounds.getLength(); i++) {
      const point = [
        polygonBounds.getAt(i).lng(),
        polygonBounds.getAt(i).lat(),
      ];
      bounds.push(point);
    }
    if (bounds[0] != bounds[bounds.length - 1]) {
      bounds.push(bounds[0]);
    }
    if (onPolygonComplete) {
      if (allowMultiple && polygonData?.type === 'MultiPolygon') {
        // Add new polygon to existing MultiPolygon
        const newPolygons: number[][][][] = [
          ...polygonData.coordinates,
          [bounds],
        ];
        onPolygonComplete({
          type: 'MultiPolygon',
          coordinates: newPolygons as any,
        });
      } else if (allowMultiple && polygonData?.type === 'Polygon') {
        // Convert existing Polygon to MultiPolygon and add new polygon
        // Transfer existing Polygon data into MultiPolygon structure
        const existingPolygonCoords = polygonData.coordinates;
        onPolygonComplete({
          type: 'MultiPolygon',
          coordinates: [existingPolygonCoords, [bounds]] as any,
        });
      } else if (allowMultiple) {
        // Create new MultiPolygon with just the new polygon
        onPolygonComplete({
          type: 'MultiPolygon',
          coordinates: [[bounds]] as any,
        });
      } else {
        // Default: single Polygon (replace existing data)
        onPolygonComplete({ type: 'Polygon', coordinates: [bounds] });
      }
      polygon.setPaths([[]]);
    }
  };

  const handlePolygonCompleteRef = useRef(handlePolygonComplete);
  handlePolygonCompleteRef.current = handlePolygonComplete;

  const stopDrawing = (commit: boolean) => {
    // A double-click fires two click events at the same spot first, so drop
    // consecutive duplicate vertices before committing
    const path = verticesRef.current.filter(
      (point, index, all) =>
        index === 0 ||
        point.lat !== all[index - 1].lat ||
        point.lng !== all[index - 1].lng,
    );
    setIsDrawing(false);
    setVertices([]);
    setCursor(null);
    if (commit && path.length >= 3) {
      handlePolygonCompleteRef.current(
        new google.maps.Polygon({ paths: path }),
      );
    }
  };
  const stopDrawingRef = useRef(stopDrawing);
  stopDrawingRef.current = stopDrawing;

  // Add the "Draw polygon" button to the map's top-center controls
  useEffect(() => {
    if (!map) return;

    const controlDiv = document.createElement('div');
    controlDiv.style.margin = '10px';
    const button = document.createElement('button');
    button.type = 'button';
    button.title = 'Click to add points, double-click to finish, Esc to cancel';
    Object.assign(button.style, {
      background: '#fff',
      border: 'none',
      borderRadius: '2px',
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
      color: '#666',
      cursor: 'pointer',
      font: '500 14px Roboto, Arial, sans-serif',
      padding: '8px 12px',
    });
    button.textContent = isDrawingRef.current
      ? 'Finish drawing'
      : 'Draw polygon';
    button.onclick = () => {
      if (isDrawingRef.current) {
        stopDrawingRef.current(true);
      } else {
        setIsDrawing(true);
      }
    };
    controlDiv.appendChild(button);
    buttonRef.current = button;

    const controls = map.controls[google.maps.ControlPosition.TOP_CENTER];
    controls.push(controlDiv);

    return () => {
      buttonRef.current = null;
      for (let i = controls.getLength() - 1; i >= 0; i--) {
        if (controls.getAt(i) === controlDiv) {
          controls.removeAt(i);
        }
      }
    };
  }, [map]);

  useEffect(() => {
    isDrawingRef.current = isDrawing;
    const button = buttonRef.current;
    if (button) {
      button.textContent = isDrawing ? 'Finish drawing' : 'Draw polygon';
      button.style.color = isDrawing ? colors.primary.main : '#666';
    }
  }, [isDrawing]);

  // While drawing: collect vertices from map clicks, finish on double-click,
  // cancel on Escape
  useEffect(() => {
    if (!map || !isDrawing) return;

    map.setOptions({
      draggableCursor: 'crosshair',
      disableDoubleClickZoom: true,
    });

    const listeners = [
      map.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          const point = event.latLng.toJSON();
          setVertices((prev) => [...prev, point]);
        }
      }),
      map.addListener('mousemove', (event: google.maps.MapMouseEvent) => {
        if (event.latLng) setCursor(event.latLng.toJSON());
      }),
      map.addListener('dblclick', () => stopDrawingRef.current(true)),
    ];
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') stopDrawingRef.current(false);
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      listeners.forEach((listener) => listener.remove());
      document.removeEventListener('keydown', handleKeyDown);
      map.setOptions({ draggableCursor: null, disableDoubleClickZoom: false });
    };
  }, [map, isDrawing]);

  // Render all polygons for MultiPolygon; freeze them while drawing so they
  // don't swallow the clicks that add vertices
  const renderPolygons = () => {
    if (!polygonData) return null;

    if (polygonData.type === 'Polygon') {
      return (
        <PolygonF
          editable={!isDrawing}
          paths={polygonPaths}
          options={{ ...polygonOptions, clickable: !isDrawing }}
          onEdit={handlePolygonComplete}
        />
      );
    } else {
      // Render all polygons in MultiPolygon
      return polygonData.coordinates.map((polygon, index) => {
        const paths = polygon[0].map((coordinate) => ({
          lng: coordinate[0],
          lat: coordinate[1],
        }));
        return (
          <PolygonF
            key={index}
            editable={!isDrawing && index === 0}
            paths={paths}
            options={{ ...polygonOptions, clickable: !isDrawing }}
            onEdit={handlePolygonComplete}
          />
        );
      });
    }
  };

  return (
    <>
      {isDrawing && vertices.length > 0 && (
        <PolylineF
          path={cursor ? [...vertices, cursor] : vertices}
          options={{
            strokeColor: colors.primary.main,
            clickable: false,
            zIndex: 2,
          }}
        />
      )}
      {renderPolygons()}
    </>
  );
};
